import { useState } from "react";
import {
  Form,
  Button,
  Alert,
  Spinner,
  ListGroup,
  Badge,
} from "react-bootstrap";
import { getTeamsUrl } from "../../config/api";
import { apiPost } from "../../utils/apiClient";

interface TeamsStepProps {
  onNext: () => void;
  seasonId: number;
  leagueId: number;
  token: string;
  leagueAbbr: string;
}

interface Team {
  id?: number;
  name: string;
}

function TeamsStep({
  onNext,
  seasonId,
  leagueId,
  token,
  leagueAbbr,
}: TeamsStepProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamName, setTeamName] = useState("");

  const handleAddTeam = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");

    if (!teamName || teamName.length < 2) {
      setErrorMessage("Team name must be at least 2 characters");
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiPost(
        getTeamsUrl(seasonId),
        { team: { name: teamName } },
        { token, leagueAbbr },
      );

      setTeams([...teams, result]);
      setTeamName("");
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "An error occurred";
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onNext();
  };

  const handleFinish = () => {
    onNext();
  };

  return (
    <div>
      <h4 className="mb-3">Add Teams</h4>
      <p className="text-muted mb-4">
        Add teams to your season. You can skip this step and add teams later.
      </p>

      {errorMessage && <Alert variant="warning">{errorMessage}</Alert>}

      {teams.length > 0 && (
        <div className="mb-4">
          <h6 className="mb-2">
            Teams Added <Badge bg="primary">{teams.length}</Badge>
          </h6>
          <ListGroup>
            {teams.map((team, index) => (
              <ListGroup.Item key={index}>{team.name}</ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}

      <Form onSubmit={handleAddTeam} className="mb-3">
        <Form.Group className="mb-3" controlId="formTeamName">
          <Form.Label>Team Name</Form.Label>
          <Form.Control
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Enter team name"
            disabled={isLoading}
          />
        </Form.Group>

        <Button
          variant="secondary"
          type="submit"
          disabled={isLoading}
          className="w-100"
        >
          {isLoading && (
            <Spinner animation="border" size="sm" className="me-2" />
          )}
          Add Team
        </Button>
      </Form>

      <div className="d-grid gap-2">
        {teams.length > 0 ? (
          <Button variant="primary" onClick={handleFinish}>
            Finish Setup
          </Button>
        ) : (
          <Button variant="outline-secondary" onClick={handleSkip}>
            Skip - Add Teams Later
          </Button>
        )}
      </div>
    </div>
  );
}

export default TeamsStep;
